import { ValueFormatterFunc, ValueFormatterParams } from 'ag-grid-enterprise';

export interface NumericFormatting {
    valueFormatter: ValueFormatterFunc;
    displayAsNegative: (value: unknown) => boolean;
}

function createNumericFormatting(fractionDigits: number, options: Intl.NumberFormatOptions = {}): NumericFormatting {
    const formatter = new Intl.NumberFormat('en-US', {
        ...options,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
    });

    const formattedZero = formatter.format(0);
    const formatAbsoluteValue = (value: number) => formatter.format(Math.abs(value));

    const displayAsNegative = (value: unknown): boolean => {
        if (typeof value !== 'number' || value >= 0) {
            return false;
        }

        return formatAbsoluteValue(value) !== formattedZero;
    };

    const valueFormatter: ValueFormatterFunc = params => {
        if (params.value == null) {
            return '';
        }

        const formattedValue = formatAbsoluteValue(params.value);

        return displayAsNegative(params.value)
            ? `(${formattedValue})`
            : formattedValue;
    };

    return {
        valueFormatter,
        displayAsNegative
    };
}

export const numericFormatting = {
    currency: (fractionDigits: number): NumericFormatting => {
        const formatterOptions: Intl.NumberFormatOptions = {
            style: 'currency',
            currency: 'USD'
        };

        return createNumericFormatting(fractionDigits, formatterOptions);
    },

    number: (fractionDigits: number): NumericFormatting => {
        return createNumericFormatting(fractionDigits);
    },

    percentage: (fractionDigits: number): NumericFormatting => {
        const formatterOptions: Intl.NumberFormatOptions = {
            style: 'percent'
        };

        return createNumericFormatting(fractionDigits, formatterOptions);
    }
};

export const valueFormatters = {
    currency: (fractionDigits: number): ValueFormatterFunc => {
        return numericFormatting.currency(fractionDigits).valueFormatter;
    },

    date: (params: ValueFormatterParams) => {
        return params.value == null ? '' : new Date(params.value).toLocaleDateString('en-US', {year: 'numeric', month: '2-digit', day: '2-digit',  });
    },

    dateTime: (params: ValueFormatterParams) => {
        if (params.value == null) {
            return '';
        }

        const date = new Date(params.value);
        const dateOnly = date.toLocaleDateString('en-us', {year: 'numeric',  month: '2-digit', day: '2-digit'})
        const time = date.toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit', });

        return `${dateOnly} ${time}`;
    },

    number: (fractionDigits: number): ValueFormatterFunc => {
        return numericFormatting.number(fractionDigits).valueFormatter;
    },

    percentage: (fractionDigits: number): ValueFormatterFunc => {
        return numericFormatting.percentage(fractionDigits).valueFormatter;
    },

    time: (params: ValueFormatterParams) => {
        return params.value == null ? '' : new Date(params.value).toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit', });
    }
};
